/**
 * Unified Photo Resolver (STAGSTER V2 / Neon)
 *
 * Second arg is a legacy optional overlay (always null from current API).
 * Order: optional legacy URL → users table / unified → OAuth metadata.
 */

/**
 * Get the preferred photo URL for a user
 * @param {Object} user - The user object from auth context
 * @param {Object|null} legacyProfileOverlay - Unused in V2; pass null
 * @returns {string|null} The best available photo URL
 */
export const getPreferredPhoto = (user, legacyProfileOverlay) => {
  if (legacyProfileOverlay?.profile_photo_url) {
    return optimizeImageUrl(legacyProfileOverlay.profile_photo_url);
  }

  // Priority 2: User profile image (from users table) - OVERRIDE OAUTH
  if (user?.profile_image_url) {
    return optimizeImageUrl(user.profile_image_url);
  }

  // Priority 3: OAuth avatar (LinkedIn/Google) - FALLBACK ONLY
  if (user?.user_metadata?.avatar_url) {
    return optimizeImageUrl(user.user_metadata.avatar_url);
  }

  // Priority 4: OAuth picture (alternative field) - FALLBACK ONLY
  if (user?.user_metadata?.picture) {
    return optimizeImageUrl(user.user_metadata.picture);
  }

  // No photo available
  return null;
};

/**
 * Get user initials as fallback when no photo is available
 * @param {Object} user - The user object
 * @param {Object} fridaySwipeProfile - The FridaySwipe profile data
 * @returns {string} User initials (1-2 characters)
 */
export const getUserInitials = (user, fridaySwipeProfile) => {
  // Try FridaySwipe profile name first
  if (fridaySwipeProfile?.full_name) {
    return fridaySwipeProfile.full_name
      .split(' ')
      .map(name => name[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }
  
  // Fallback to user metadata
  if (user?.user_metadata?.full_name) {
    return user.user_metadata.full_name
      .split(' ')
      .map(name => name[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }
  
  // Fallback to email
  if (user?.email) {
    const localPart = user.email.split('@')[0] || '';
    const cleaned = localPart.replace(/[^a-z0-9]/gi, '').toUpperCase();
    if (cleaned.length >= 2) return cleaned.slice(0, 2);
    if (cleaned.length === 1) return cleaned;
    return (localPart.charAt(0) || 'u').toUpperCase();
  }
  
  // Ultimate fallback
  return 'U';
};

/**
 * Check if a photo URL is valid and accessible
 * @param {string} url - The photo URL to check
 * @returns {boolean} Whether the URL appears valid
 */
export const isValidPhotoUrl = (url) => {
  if (!url || typeof url !== 'string' || url.trim() === '') return false;
  
  // Basic URL validation
  try {
    // Accept absolute URLs
    new URL(url);
    return true;
  } catch {
    // Also accept common relative/storage URLs and data/blob URIs used in-app
    if (url.startsWith('/') || url.startsWith('data:') || url.startsWith('blob:')) return true;
    return false;
  }
};

/**
 * Get photo with fallback handling
 * @param {Object} user - The user object
 * @param {Object} fridaySwipeProfile - The FridaySwipe profile data
 * @returns {Object} { photoUrl, initials, hasPhoto }
 */
export const getPhotoWithFallback = (user, fridaySwipeProfile) => {
  const photoUrl = getPreferredPhoto(user, fridaySwipeProfile);
  const initials = getUserInitials(user, fridaySwipeProfile);
  // Extra validation: ensure photoUrl is not empty even if it passed basic validation
  const hasPhoto = isValidPhotoUrl(photoUrl) && photoUrl && photoUrl.trim() !== '';
  
  return {
    photoUrl: hasPhoto ? photoUrl : null,
    initials,
    hasPhoto
  };
};

/**
 * Optimize image URLs for performance. Pass-through for OAuth/CDN images.
 * STAGSTER V2 no longer rewrites legacy Supabase storage URLs.
 */
export const optimizeImageUrl = (url, width = 200, height = 200, quality = 80) => {
  try {
    if (!url || typeof url !== 'string') return url;
    const lower = url.toLowerCase();
    // Trusted CDNs already optimized
    if (
      lower.includes('googleusercontent.com') ||
      lower.includes('fbcdn.net') ||
      lower.includes('cdn.linkedin') ||
      lower.includes('licdn.com')
    ) {
      return url;
    }
    return url;
  } catch (_) {
    return url;
  }
};

/**
 * Build srcSet string for responsive images using optimizeImageUrl.
 */
export const buildSrcSet = (url, widths = [64, 128, 256], square = true, quality = 80) => {
  try {
    const set = widths.map(w => {
      const h = square ? w : undefined;
      const src = optimizeImageUrl(url, w, h ?? w, quality);
      return `${src} ${w}w`;
    });
    return set.join(', ');
  } catch (_) {
    return '';
  }
};

/**
 * Latest pending profile photo in the manual review queue (admin approval).
 */
export function getLatestPendingProfilePhotoReview(reviews) {
  if (!Array.isArray(reviews) || reviews.length === 0) return null;
  const pending = reviews.filter(
    (r) => r.status === 'pending' && (!r.photo_type || r.photo_type === 'profile'),
  );
  if (!pending.length) return null;
  return pending.reduce((best, r) => {
    const tb = new Date(r.submitted_at || r.created_at || 0).getTime();
    const ta = new Date(best.submitted_at || best.created_at || 0).getTime();
    return tb >= ta ? r : best;
  });
}

/**
 * Gmail-style initials from a display name and optional email (same rules as the header chip).
 * Use for form drafts so edit screen matches SharedHeader / slideout.
 */
export function initialsFromDisplayName(name, email = '') {
  const trimmed = String(name || '').trim();
  if (trimmed) {
    const parts = trimmed.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return trimmed.slice(0, 2).toUpperCase();
  }
  const em = String(email || '').trim();
  if (em) {
    const localPart = em.split('@')[0] || '';
    const cleaned = localPart.replace(/[^a-z0-9]/gi, '').toUpperCase();
    if (cleaned.length >= 2) return cleaned.slice(0, 2);
    if (cleaned.length === 1) return cleaned;
    return (localPart.charAt(0) || '?').toUpperCase();
  }
  return '?';
}

/**
 * Gmail-style initials: name first, then email local-part, then "?".
 */
export function getAccountInitials(user, unifiedUser, fridaySwipeProfile) {
  const name =
    fridaySwipeProfile?.full_name ||
    unifiedUser?.full_name ||
    user?.full_name ||
    user?.user_metadata?.full_name ||
    user?.user_metadata?.name ||
    '';
  const email = unifiedUser?.email || user?.email || '';
  return initialsFromDisplayName(name, email);
}

/**
 * Header / account menu: if a new upload is in the review queue, show that image to the signed-in
 * owner (with a pending badge in the header). Hiding it entirely made avatars "disappear" after
 * save until an admin approved. If the URL is not usable, fall back to the normal chain.
 */
export function resolveAccountAvatarDisplay({
  user = null,
  unifiedUser = null,
  fridaySwipeProfile = null,
  photoReviews = [],
} = {}) {
  let pending = getLatestPendingProfilePhotoReview(photoReviews);
  // If the live Stag avatar already matches this pending row, treat as resolved (stuck queue / desync).
  const pendingUrl = pending?.photo_url != null ? String(pending.photo_url).trim() : '';
  const liveAvatar =
    unifiedUser?.avatar_url != null ? String(unifiedUser.avatar_url).trim() : '';
  if (pending && pendingUrl && liveAvatar && pendingUrl === liveAvatar) {
    pending = null;
  }

  const initials = getAccountInitials(user, unifiedUser, fridaySwipeProfile);

  if (pending && pendingUrl) {
    const candidate = optimizeImageUrl(pendingUrl);
    if (candidate && isValidPhotoUrl(candidate)) {
      return {
        photoUrl: candidate,
        initials,
        hasPhoto: true,
        isPendingReview: true,
      };
    }
  }

  let photoUrl = null;
  // Prefer canonical users.avatar_url (admin-approved uploads) over FridaySwipe copy, which can lag or expire.
  if (unifiedUser?.avatar_url) {
    photoUrl = optimizeImageUrl(unifiedUser.avatar_url);
  } else if (fridaySwipeProfile?.profile_photo_url) {
    photoUrl = optimizeImageUrl(fridaySwipeProfile.profile_photo_url);
  } else if (user?.profile_image_url) {
    photoUrl = optimizeImageUrl(user.profile_image_url);
  } else if (user?.user_metadata?.avatar_url) {
    photoUrl = optimizeImageUrl(user.user_metadata.avatar_url);
  } else if (user?.user_metadata?.picture) {
    photoUrl = optimizeImageUrl(user.user_metadata.picture);
  }

  const hasPhoto = !!(photoUrl && isValidPhotoUrl(photoUrl));
  return {
    photoUrl: hasPhoto ? photoUrl : null,
    initials,
    hasPhoto,
    isPendingReview: false,
  };
}
